import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getOrders } from '@/lib/api';
import { Package, ChevronRight, Clock, Truck, CheckCircle, FileText } from 'lucide-react';

const STATUS_MAP = {
  placed: { label: 'Commande passée', color: 'bg-blue-100 text-blue-700', icon: Clock },
  sent_to_supplier: { label: 'Envoyée au fournisseur', color: 'bg-yellow-100 text-yellow-700', icon: Package },
  delivery_confirmed: { label: 'Date livraison confirmée', color: 'bg-orange-100 text-orange-700', icon: Truck },
  in_transit: { label: 'En transit', color: 'bg-purple-100 text-purple-700', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  invoiced: { label: 'Facturée', color: 'bg-gray-100 text-gray-700', icon: FileText },
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOrders().then(r => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="orders-page" className="min-h-screen">
      <div className="border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00A5FF] font-semibold mb-2">Mon compte</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tight">Mes commandes</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-[#F8F9FA] animate-pulse border border-black/10" />)}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 border border-black/10">
            <Package size={48} className="mx-auto text-black/10 mb-4" />
            <p className="text-sm text-black/50">Aucune commande</p>
            <Link to="/catalog" className="text-sm text-[#00A5FF] hover:underline mt-2 inline-block">Parcourir le catalogue</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const st = STATUS_MAP[order.status] || STATUS_MAP.placed;
              return (
                <div key={order.id} data-testid={`order-${order.id}`} className="border border-black/10 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <p className="text-sm font-bold text-[#111]">{order.order_number}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm ${st.color}`}>
                          <st.icon size={12} />
                          {st.label}
                        </span>
                      </div>
                      <p className="text-xs text-black/40">{new Date(order.created_at).toLocaleDateString('fr-BE', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      <p className="text-xs text-black/40">{order.items?.length || 0} article{(order.items?.length || 0) !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-extrabold text-[#111]">{order.total?.toFixed(2)} EUR</p>
                      <p className="text-xs text-black/40">Acompte: {order.deposit?.toFixed(2)} EUR</p>
                    </div>
                  </div>
                  {order.estimated_delivery_date && (
                    <p className="text-xs text-[#00A5FF] mt-2 flex items-center gap-1">
                      <Truck size={12} /> Livraison estimée: {order.estimated_delivery_date}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
